// API client configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:6001';
const isDebugEnabled = import.meta.env.VITE_DEBUG === 'true' || import.meta.env.DEV;

// Import user date utilities
import { getUserDateSafe } from '@/utils/userDate';

// Types
export type GetTokenFunction = (options?: { skipCache?: boolean }) => Promise<string | null>;
export type SignOutFunction = () => void;
export type ShowErrorFunction = (message: string) => void;

// Response types
export interface HealthCheckResponse {
  api: string;
  database: string;
  timestamp: string;
  service: string;
  version: string;
  environment: string;
  packageVersion: string;
  uptime: number;
  responseTime: {
    api: number;
    database: number;
  };
}

export interface ApiError {
  message: string;
  status: number;
  error?: string;
}

// Import and re-export types from centralized location
import type { 
  Game, 
  GameChallenge, 
  PlayerProgress, 
  ChallengeWithProgress, 
  ProgressDisplayState,
  GetChallengesResponse,
  GetProgressResponse,
  ChallengeContextState,
  NonAuthDisplay
} from '@/types/game';

import type {
  Section,
  PublicSection,
  SectionWithGames,
  GetSectionsResponse,
  GetSectionResponse,
  SectionType,
  SectionDisplayState
} from '@/types/section';

export type {
  Game, 
  GameChallenge, 
  PlayerProgress, 
  ChallengeWithProgress, 
  ProgressDisplayState,
  GetChallengesResponse,
  GetProgressResponse,
  ChallengeContextState,
  NonAuthDisplay,
  Section,
  PublicSection,
  SectionWithGames,
  GetSectionsResponse,
  GetSectionResponse,
  SectionType,
  SectionDisplayState
};

export interface GamesResponse {
  games: Game[];
  count: number;
}

export interface PlayerStatsResponse {
  gameId: string;
  userId: string;
  stats: {
    currentStreak: number;
    averageScore: number;
    successRate: number; // percentage 0-100
    totalPlayed: number;
    totalStars: number; // IMA-97: Total stars earned across all challenges for this game
    lastPlayed?: string; // ISO date
  };
  currentChallenge: {
    id: string;
    puzzleName: string;
    puzzleNumber: number;
    publishDate: string;
    playerStatus: 'not_started' | 'in_progress' | 'completed';
    score?: number;
    starsEarned?: number;
  };
  // REMOVED: averageSolve - now fetched from challenge detail endpoint
}

export interface H2HStatsResponse {
  gameId: string;
  userId: string;
  stats: {
    activeGames: number;
    winPercentage: number;
    wins: number | null; // null = doesn't apply to game type
    losses: number | null; // null = doesn't apply to game type
    averageGamesPlayed: number;
  };
}

export interface ChallengeDetailResponse {
  id: string;
  gameId: string;
  publishDate: string;
  puzzleNumber: number;
  puzzleName: string;
  averageSolve: number;     // Community average for this challenge
  totalPlayers: number;     // How many have attempted
  completionRate: number;   // Percentage who completed
}

export interface ChallengeProgressResponse {
  activityState: 'not_played' | 'in_progress' | 'completed';
  starsEarned: number;
  completedAt?: string;     // ISO date string
}

// Archive calendar types
export interface ArchiveChallengeStatus {
  id: number;
  gameId: string;
  publishDate: string;      // ISO date string
  puzzleNumber: number;
  puzzleName: string;
  status: 'today' | 'completed' | 'locked' | 'future' | 'available';
  displayState: 'playable' | 'locked' | 'display-only';  // Backend authority for access decisions
  userProgress?: {
    starsEarned: number;
    completedAt: string | null;  // ISO date string
    score: number | null;
  };
}

export interface ArchiveCalendarResponse {
  challenges: ArchiveChallengeStatus[];
}

// Public settings response
export interface PublicSettingsResponse {
  paywall: boolean;
}

export interface TrialStatus {
  active: boolean;
  trialEnd: string | null;
  daysRemaining: number | null;
}

// API client class
class ApiClient {
  private baseUrl: string;
  private defaultTimeout: number = 30000; // 30 seconds default timeout
  private getToken: GetTokenFunction | null = null;
  private signOut: SignOutFunction | null = null;
  private showError: ShowErrorFunction | null = null;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Set the token getter function (called once when the app initializes)
  setTokenGetter(getToken: GetTokenFunction | null) {
    this.getToken = getToken;
  }

  // Set the sign out function for clearing auth state
  setSignOutFunction(signOut: SignOutFunction | null) {
    this.signOut = signOut;
  }

  // Set the error display function
  setErrorHandler(showError: ShowErrorFunction | null) {
    this.showError = showError;
  }

  // Add user date header to challenge-related requests
  private addUserDateHeader(headers: Record<string, string> = {}): Record<string, string> {
    try {
      const { userDate, success, error } = getUserDateSafe();
      
      if (success) {
        return {
          ...headers,
          'X-User-Date': userDate
        };
      } else {
        // Log the error but still add the fallback date
        if (isDebugEnabled) {
          console.warn('⚠️ User date calculation failed, using fallback:', error);
        }
        return {
          ...headers,
          'X-User-Date': userDate // getUserDateSafe returns fallback date even on failure
        };
      }
    } catch (error) {
      // Fallback behavior if everything fails
      if (isDebugEnabled) {
        console.error('❌ Critical error in user date calculation:', error);
      }
      // Still provide a header with server date as ultimate fallback
      const fallbackDate = new Date().toISOString().split('T')[0];
      return {
        ...headers,
        'X-User-Date': fallbackDate
      };
    }
  }

  // Generic request method with error handling and timeout
  private async request<T>(
    endpoint: string,
    options: RequestInit & { silent?: boolean } = {},
    timeout?: number
  ): Promise<T> {
    const { silent, ...fetchOptions } = options;
    const url = `${this.baseUrl}${endpoint}`;
    const requestTimeout = timeout || this.defaultTimeout;
    
    // Create AbortController for timeout
    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, requestTimeout);
    
    // Combine external signal with timeout signal if provided
    let signal = controller.signal;
    if (fetchOptions.signal) {
      // Create a new AbortController that listens to both signals
      const combinedController = new AbortController();

      // Abort if external signal aborts
      fetchOptions.signal.addEventListener('abort', () => {
        combinedController.abort();
        clearTimeout(timeoutId);
      });
      
      // Abort if timeout occurs
      controller.signal.addEventListener('abort', () => {
        combinedController.abort();
      });
      
      signal = combinedController.signal;
    }
    
    try {
      // Convert headers to plain object if it's a Headers instance
      let plainHeaders: Record<string, string> = {};
      if (fetchOptions.headers instanceof Headers) {
        // Convert Headers instance to plain object
        fetchOptions.headers.forEach((value, key) => {
          plainHeaders[key] = value;
        });
      } else if (fetchOptions.headers) {
        // Already a plain object
        plainHeaders = fetchOptions.headers as Record<string, string>;
      }

      // Build headers with authentication if available
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...plainHeaders,
      };

      // Add auth token if available and not explicitly disabled
      const authHeader = plainHeaders['Authorization'];
      if (this.getToken && authHeader !== 'none') {
        const token = await this.getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      // Remove the special 'none' authorization header if present
      if (authHeader === 'none') {
        delete headers['Authorization'];
      }

      const response = await fetch(url, {
        ...fetchOptions,
        signal,
        headers,
      });

      // Clear timeout on successful response
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: `HTTP ${response.status}: ${response.statusText}`,
          status: response.status,
        }));
        
        // Handle 401 unauthorized - attempt token refresh once
        if (response.status === 401 && this.getToken && !(options.headers as Record<string, string>)?.['X-Retry-Auth']) {
          clearTimeout(timeoutId);
          
          // Force token refresh
          const newToken = await this.getToken({ skipCache: true });
          if (newToken) {
            // Retry the request with the new token
            return this.request<T>(endpoint, {
              ...options,
              headers: {
                ...options.headers,
                'X-Retry-Auth': 'true', // Prevent infinite retry loop
              },
            }, timeout);
          } else {
            // Token refresh failed, clear auth state
            if (this.signOut) {
              this.signOut();
            }
            // Show user-friendly error message
            if (this.showError) {
              this.showError('Your session has expired. Please sign in again.');
            }
          }
        }
        
        // Handle 400 Bad Request with date validation errors - attempt retry with fresh user date once
        if (response.status === 400 && 
            errorData.message && 
            (errorData.message.includes('date') || errorData.message.includes('User date')) &&
            !(options.headers as Record<string, string>)?.['X-Retry-Date']) {
          
          clearTimeout(timeoutId);
          
          // Retry the request with a fresh user date
          const originalHeaders = (options.headers as Record<string, string>) || {};
          const headersWithoutUserDate = Object.fromEntries(
            Object.entries(originalHeaders).filter(([key]) => key !== 'X-User-Date')
          );
          
          return this.request<T>(endpoint, {
            ...options,
            headers: {
              ...headersWithoutUserDate,
              ...this.addUserDateHeader(), // Get fresh user date
              'X-Retry-Date': 'true', // Prevent infinite retry loop
            },
          }, timeout);
        }
        
        // Show appropriate error messages based on status code
        if (!silent && this.showError && response.status !== 401) {
          switch (response.status) {
            case 403:
              this.showError('You do not have permission to perform this action.');
              break;
            case 404:
              this.showError('The requested resource could not be found.');
              break;
            case 429:
              this.showError('Too many requests. Please try again later.');
              break;
            case 500:
            case 502:
            case 503:
              this.showError('Server error. Please try again later.');
              break;
            default:
              if (errorData.message && !errorData.message.includes('HTTP')) {
                this.showError(errorData.message);
              }
          }
        }
        
        throw {
          message: errorData.message || `Request failed with status ${response.status}`,
          status: response.status,
          error: errorData.error,
        } as ApiError;
      }

      return await response.json();
    } catch (error) {
      // Clear timeout on error
      clearTimeout(timeoutId);
      
      // Handle abort — distinguish genuine timeout from caller-initiated cancel
      if (error instanceof Error && error.name === 'AbortError') {
        if (timedOut && !silent && this.showError) {
          this.showError(`Request timed out. Please check your connection and try again.`);
        }
        throw {
          message: timedOut
            ? `Request timeout: Server did not respond within ${requestTimeout / 1000} seconds`
            : 'Request cancelled',
          status: timedOut ? 408 : 499,
          error: timedOut ? 'TIMEOUT' : 'CANCELLED',
        } as ApiError;
      }
      
      // Network errors or other fetch failures
      if ((error as ApiError).status) {
        throw error; // Re-throw API errors
      }

      // Show network error message
      if (!silent && this.showError) {
        this.showError('Unable to connect to the server. Please check your internet connection.');
      }
      
      throw {
        message: 'Network error: Unable to connect to the server',
        status: 0,
        error: error instanceof Error ? error.message : 'Unknown error',
      } as ApiError;
    }
  }

  // Health check endpoint with shorter timeout
  async checkHealth(): Promise<HealthCheckResponse> {
    return this.request<HealthCheckResponse>('/health', { silent: true }, 10000); // 10 second timeout for health checks
  }

  // Future method placeholders with auth interceptor support
  async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'GET',
    });
  }

  async post<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'DELETE',
    });
  }

  // Get the current API base URL (useful for debugging)
  getBaseUrl(): string {
    return this.baseUrl;
  }

  // Public settings endpoint (no authentication required)
  async getPublicSettings(): Promise<PublicSettingsResponse> {
    return this.get<PublicSettingsResponse>('/api/v1/settings/public');
  }

  // Guest token endpoint — unauthenticated, no Clerk Bearer token
  async getGuestToken(existingUserId?: string): Promise<{ success: true; token: string; userId: string }> {
    const headers: Record<string, string> = {
      Authorization: 'none', // suppress Clerk token injection
    };
    if (existingUserId) {
      headers['X-Guest-User-Id'] = existingUserId;
    }
    return this.request<{ success: true; token: string; userId: string }>(
      '/api/v1/games/guest-token',
      { method: 'POST', headers },
    );
  }

  // Game-related endpoints
  async getGames(): Promise<GamesResponse> {
    const response = await this.get<{ success: boolean; data: Game[]; meta: { count: number } }>('/api/v1/games');
    return {
      games: response.data,
      count: response.meta.count
    };
  }

  async getGame(gameId: string, options?: RequestInit): Promise<Game> {
    if (!gameId || !gameId.match(/^[a-zA-Z0-9_]+$/)) {
      throw {
        message: 'Invalid game ID format',
        status: 400,
        error: 'INVALID_GAME_ID'
      } as ApiError;
    }
    const response = await this.get<{ success: boolean; data: Game }>(`/api/v1/games/${gameId}`, options);
    return response.data;
  }

  // Challenge-related endpoints
  async getChallengesForGame(gameId: string): Promise<GameChallenge[]> {
    if (!gameId || !gameId.match(/^[a-zA-Z0-9_]+$/)) {
      throw {
        message: 'Invalid game ID format',
        status: 400,
        error: 'INVALID_GAME_ID'
      } as ApiError;
    }
    const response = await this.get<GetChallengesResponse>(`/api/v1/games/${gameId}/challenges`, {
      headers: this.addUserDateHeader()
    });
    return response.data;
  }

  // Unified progress endpoint (works for authenticated, anonymous, and guest users)
  async getProgress(gameId: string, guestToken?: string): Promise<ChallengeWithProgress[]> {
    if (!gameId || !gameId.match(/^[a-zA-Z0-9_]+$/)) {
      throw {
        message: 'Invalid game ID format',
        status: 400,
        error: 'INVALID_GAME_ID'
      } as ApiError;
    }
    const headers: Record<string, string> = { ...this.addUserDateHeader() };
    if (guestToken) {
      headers['Authorization'] = `Bearer ${guestToken}`;
    }
    const response = await this.get<GetProgressResponse>(`/api/v1/games/${gameId}/progress`, {
      headers
    });
    return response.data;
  }

  // Player statistics endpoints
  async getPlayerStats(gameId: string): Promise<PlayerStatsResponse> {
    if (!gameId || !gameId.match(/^[a-zA-Z0-9_]+$/)) {
      throw {
        message: 'Invalid game ID format',
        status: 400,
        error: 'INVALID_GAME_ID'
      } as ApiError;
    }
    const response = await this.get<{ success: boolean; data: PlayerStatsResponse }>(`/api/v1/games/${gameId}/stats`, {
      headers: this.addUserDateHeader()
    });
    return response.data;
  }

  // H2H statistics endpoint (for Diamond Duel)
  async getH2HStats(gameId: string): Promise<H2HStatsResponse> {
    if (!gameId || !gameId.match(/^[a-zA-Z0-9_]+$/)) {
      throw {
        message: 'Invalid game ID format',
        status: 400,
        error: 'INVALID_GAME_ID'
      } as ApiError;
    }
    const response = await this.get<{ success: boolean; data: H2HStatsResponse }>(`/api/v1/games/${gameId}/stats/h2h`);
    return response.data;
  }

  // Challenge detail endpoint
  async getChallengeDetail(gameId: string, challengeId: string): Promise<ChallengeDetailResponse> {
    if (!gameId || !gameId.match(/^[a-zA-Z0-9_]+$/)) {
      throw {
        message: 'Invalid game ID format',
        status: 400,
        error: 'INVALID_GAME_ID'
      } as ApiError;
    }
    if (!challengeId) {
      throw {
        message: 'Challenge ID is required',
        status: 400,
        error: 'INVALID_CHALLENGE_ID'
      } as ApiError;
    }
    const response = await this.get<{ success: boolean; data: ChallengeDetailResponse }>(`/api/v1/games/${gameId}/challenges/${challengeId}`, {
      headers: this.addUserDateHeader()
    });
    return response.data;
  }

  // Challenge progress endpoint (authenticated only)
  async getChallengeProgress(gameId: string, challengeId: string): Promise<ChallengeProgressResponse> {
    if (!gameId || !gameId.match(/^[a-zA-Z0-9_]+$/)) {
      throw {
        message: 'Invalid game ID format',
        status: 400,
        error: 'INVALID_GAME_ID'
      } as ApiError;
    }
    if (!challengeId) {
      throw {
        message: 'Challenge ID is required',
        status: 400,
        error: 'INVALID_CHALLENGE_ID'
      } as ApiError;
    }
    const response = await this.get<{ success: boolean; data: ChallengeProgressResponse }>(`/api/v1/games/${gameId}/challenges/${challengeId}/progress`, {
      headers: this.addUserDateHeader()
    });
    return response.data;
  }

  // Archive calendar endpoint (authenticated only)
  async getArchiveCalendar(gameId: string, month: string, guestToken?: string): Promise<ArchiveCalendarResponse> {
    if (!gameId || !gameId.match(/^[a-zA-Z0-9_]+$/)) {
      throw {
        message: 'Invalid game ID format',
        status: 400,
        error: 'INVALID_GAME_ID'
      } as ApiError;
    }
    if (!month || !month.match(/^\d{4}-\d{2}$/)) {
      throw {
        message: 'Invalid month format. Expected YYYY-MM',
        status: 400,
        error: 'INVALID_MONTH_FORMAT'
      } as ApiError;
    }
    const headers: Record<string, string> = { ...this.addUserDateHeader() };
    if (guestToken) headers['Authorization'] = `Bearer ${guestToken}`;
    const response = await this.get<{ success: boolean; data: ArchiveCalendarResponse }>(`/api/v1/games/${gameId}/archive/calendar?month=${month}`, {
      headers
    });
    return response.data;
  }

  // Section-related endpoints (public, no authentication required)
  async getSections(): Promise<SectionWithGames[]> {
    // Use Authorization: none to bypass automatic token addition
    const response = await this.get<GetSectionsResponse>('/api/v1/sections', {
      headers: { Authorization: 'none' }
    });
    return response.data;
  }

  async getSectionById(sectionId: number): Promise<SectionWithGames> {
    if (!sectionId || sectionId <= 0) {
      throw {
        message: 'Invalid section ID',
        status: 400,
        error: 'INVALID_SECTION_ID'
      } as ApiError;
    }
    // Use Authorization: none to bypass automatic token addition
    const response = await this.get<GetSectionResponse>(`/api/v1/sections/${sectionId}`, {
      headers: { Authorization: 'none' }
    });
    return response.data;
  }

  async getTrialStatus(): Promise<TrialStatus> {
    const response = await this.get<{ success: boolean; data: TrialStatus }>('/api/v1/user/trial-status');
    return response.data;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export for use in other components
export default apiClient;